'use client';
import SelectedPostImages from '@components/PostImageSelector/SelectedPostImages';
import SelectedPostVideos from '@components/PostVideoSelector/SelectedPostVideos';
import {
  Autocomplete,
  Box,
  FormControlLabel,
  InputAdornment,
  Stack,
  styled,
  Switch,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { setHours, setMinutes } from 'date-fns';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useStore from '../../stores/store';
import PostViewersSelector from '../PostViewerSelector/PostViewersSelector';
import StyledInput from '../Styled/styledInput';
import Input from '../next-ui/Input';
import TextArea from '../next-ui/TextArea';
import getSymbolFromCurrency from 'currency-symbol-map';
import { getUserCountry, getUserCurrency } from '@/utils/user.helper';
import PostNotify from '../next-page-components/PostNotify';
import PostType from '@/constants/postType.constants';
import OrgType from '@/constants/orgType.constant';
import BillingRates from '@/constants/VolunteeringBillingRates.constant';

const WrappingRowContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'stretch',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

function VolunteeringForm(props) {
  const { skills, causes, images, videos, isEditMode } = props;
  const userProfile = useStore((state) => state.user.profile);
  const methods = useFormContext();
  const country = getUserCountry(userProfile);

  const estimatedBillRate = BillingRates[country];

  const { control, register, formState, watch, setValue, trigger, getValues } =
    methods;
  const { dirtyFields } = formState;
  const { errors } = formState;
  const [
    isOneTime,
    isVirtual,
    startsOn,
    startsAt,
    endsAt,
    timeCommitmentMin,
    endsOn,
  ] = watch([
    'isOneTime',
    'virtual',
    'startsOn',
    'startsAt',
    'endsAt',
    'timeCommitmentMin',
    'endsOn',
  ]);

  // set endsOn to startsOn if isOneTime is true
  useEffect(() => {
    if (isOneTime) {
      setValue('endsOn', startsOn);
      const startsAtVal = setMinutes(
        setHours(new Date(startsOn), startsAt?.getHours() || 0),
        startsAt?.getMinutes() || 0,
      );
      const endsAtVal = setMinutes(
        setHours(new Date(startsOn), endsAt?.getHours() || 0),
        endsAt?.getMinutes() || 0,
      );
      setValue('startsAt', startsAtVal);
      setValue('endsAt', endsAtVal);
      setValue('type', 'one-time-event');
    } else {
      setValue('type', 'flexible');
    }

    // trigger validation
    trigger([
      'startsOn',
      'endsOn',
      'startsAt',
      'endsAt',
      'type',
      'applyDeadline',
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startsOn, isOneTime, setValue, trigger]);

  // force endsAt validation when startsAt changes
  useEffect(() => {
    if (isOneTime && dirtyFields.endsAt) {
      trigger(['endsAt', 'applyDeadline']);
    }
  }, [isOneTime, startsAt, trigger, dirtyFields]);

  // force timeCommitmentMax validation when timeCommitmentMin changes
  useEffect(() => {
    // if (!isOneTime && dirtyFields.timeCommitmentMax) {
    //   trigger('timeCommitmentMax');
    // }
    if (timeCommitmentMin) {
      setValue('timeCommitmentMax', timeCommitmentMin);
    }
    if (estimatedBillRate) {
      setValue('estimatedBillRate', estimatedBillRate);
    }
  }, [isOneTime, timeCommitmentMin, estimatedBillRate, trigger, dirtyFields]);

  // force applyDeadline validation when endsOn changes
  useEffect(() => {
    if (dirtyFields.applyDeadline) {
      trigger('applyDeadline');
    }
  }, [endsOn, trigger, dirtyFields]);

  // useEffect(() => {
  //   if (estimatedBillRate) {
  //     setValue('estimatedBillRate', estimatedBillRate);
  //   }
  // }, [estimatedBillRate]);

  const locationHelperText = isVirtual
    ? 'Enter the URL for the online meeting. You can use google meet, zoom or any other online meeting tool'
    : 'Enter the address of the volunteering location';

  return (
    <div className="flex w-full flex-col gap-6">
      <Box>
        <PostViewersSelector disabled={isEditMode} />
      </Box>

      {/* <div>
        <PostNotify
          postType={PostType.Volunteering}
          orgId={userProfile?.activeCommunity?.nonProfit?.id}
          orgType={OrgType.NonProfit}
        />
      </div> */}

      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <Input
            {...field}
            label="Name"
            placeholder="Name of the volunteering opportunity..."
            error={errors.name?.message}
            required
          />
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field }) => (
          <TextArea
            {...field}
            label="Description"
            placeholder="Provide a detailed description of this volunteering opportunity with what you would like the volunteer to help with, instructions for the volunteer to be prepared with, etc."
            rows={3}
            className="w-full"
            error={errors.description?.message}
            required
          />
        )}
      />

      <FormControlLabel
        label="IS THIS ONE-TIME EVENT?"
        labelPlacement="end"
        control={
          <Controller
            name="isOneTime"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Switch
                checked={value}
                disableRipple
                onChange={(ev) => onChange(ev.target.checked)}
              />
            )}
          />
        }
      />

      <WrappingRowContainer>
        <Controller
          name="startsOn"
          control={control}
          render={({ field: { onChange, value } }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDatePicker
                onChange={onChange}
                value={value}
                label="START DATE"
                inputFormat="MMM dd, yyyy"
                renderInput={({ ref, inputRef, ...params }) => (
                  <StyledInput
                    {...params}
                    width="100%"
                    placeholder="START DATE"
                    errorMessage={errors.startsOn?.message || ''}
                    required
                  />
                )}
              />
            </LocalizationProvider>
          )}
        />
        <Controller
          name="endsOn"
          control={control}
          render={({ field: { onChange, value } }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDatePicker
                onChange={onChange}
                value={value}
                label="END DATE"
                inputFormat="MMM dd, yyyy"
                renderInput={({ ref, inputRef, ...params }) => (
                  <StyledInput
                    {...params}
                    width="100%"
                    placeholder="END DATE"
                    errorMessage={errors.endsOn?.message || ''}
                    required
                  />
                )}
              />
            </LocalizationProvider>
          )}
        />

        {/* <Controller
          name="applyDeadline"
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDatePicker
                {...field}
                label="Last Date to Apply (Optional)"
                inputFormat="MMM dd, yyyy"
                renderInput={({ ref, inputRef, ...params }) => (
                  <StyledInput
                    {...params}
                    width="100%"
                    placeholder="Last Date to Apply (Optional)"
                    errorMessage={errors.applyDeadline?.message || ''}
                  />
                )}
              />
            </LocalizationProvider>
          )}
        /> */}
      </WrappingRowContainer>

      {isOneTime && (
        <WrappingRowContainer>
          <Controller
            name="startsAt"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MobileTimePicker
                  {...field}
                  label="STARTS AT"
                  renderInput={({ ref, inputRef, ...params }) => (
                    <StyledInput
                      {...params}
                      width="100%"
                      placeholder="STARTS AT"
                      errorMessage={errors.startsAt?.message || ''}
                      required
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />

          <Controller
            name="endsAt"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MobileTimePicker
                  {...field}
                  label="ENDS AT"
                  renderInput={({ ref, inputRef, ...params }) => (
                    <StyledInput
                      {...params}
                      width="100%"
                      placeholder="ENDS AT"
                      errorMessage={errors.endsAt?.message || ''}
                      required
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </WrappingRowContainer>
      )}

      {!isOneTime && (
        <>
          <StyledInput
            // placeholder="MINIMUM TIME COMMITMENT (HRS)"
            placeholder="TIME COMMITMENT (HRS)"
            tooltip="Minimum time you expect the volunteer to help you with this need"
            width="100%"
            register={register('timeCommitmentMin')}
            errorMessage={errors.timeCommitmentMin?.message || ''}
            required
          />

          {/* <StyledInput
            placeholder="MAXIMUM TIME COMMITMENT (HRS)"
            tooltip="Maximum time you expect the volunteer to help you with this need"
            width="100%"
            register={register('timeCommitmentMax')}
            errorMessage={errors.timeCommitmentMax?.message || ''}
            required
          /> */}
        </>
      )}

      <FormControlLabel
        label="IS THIS AN ONLINE EVENT?"
        labelPlacement="end"
        control={
          <Controller
            name="virtual"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Switch
                disableRipple
                checked={value}
                onChange={(ev) => onChange(ev.target.checked)}
              />
            )}
          />
        }
      />

      <Controller
        control={control}
        name="location"
        render={({ field }) => (
          <TextArea
            {...field}
            label="Location Details"
            placeholder={locationHelperText}
            rows={3}
            className="w-full"
            error={errors.location?.message}
            required
          />
        )}
      />

      <Controller
        control={control}
        name="additionalDetails"
        render={({ field }) => (
          <TextArea
            {...field}
            label="Additional Details"
            placeholder="Do you have any additional requirements for the volunteers? For e.g. their dressing code, preparation, interviews that you may conduct prior to selecting them, etc."
            rows={3}
            className="w-full"
            error={errors.additionalDetails?.message}
          />
        )}
      />

      <WrappingRowContainer>
        <StyledInput
          placeholder="VOLUNTEERS REQUIRED"
          width="100%"
          register={register('volunteersReq')}
          errorMessage={errors.volunteersReq?.message || ''}
          required
        />

        {/* <StyledInput
          placeholder="ESTIMATED BILLING RATE"
          tooltip="Enter the hourly amount that you would have paid someone, if they were NOT volunteering."
          startAdornment={
            <InputAdornment position="start">
              <Typography
                variant="body1"
                fontWeight={700}
                color="text.disabled">
                {getSymbolFromCurrency(getUserCurrency(userProfile))}
              </Typography>
            </InputAdornment>
          }
          width="100%"
          register={register('estimatedBillRate')}
          errorMessage={errors.estimatedBillRate?.message || ''}
          required
        /> */}
      </WrappingRowContainer>

      <WrappingRowContainer>
        <Controller
          name="mandatorySkills"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              value={value}
              onChange={(event, newValue) => {
                onChange(newValue);
              }}
              multiple
              id="mandatorySkills"
              options={skills}
              getOptionLabel={(option) => option.skill_title}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              fullWidth
              ChipProps={{
                color: 'secondary',
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="MANDATORY SKILLS"
                  placeholder="Add skills..."
                  required
                  helperText={
                    errors.mandatorySkills?.message ||
                    'Mandatory skills required for this job'
                  }
                  error={!!errors.mandatorySkills}
                  fullWidth
                />
              )}
            />
          )}
        />

        <Controller
          name="optionalSkills"
          control={control}
          defaultValue={[]}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              value={value}
              onChange={(event, newValue) => {
                onChange(newValue);
              }}
              multiple
              id="optionalSkills"
              options={skills}
              getOptionLabel={(option) => option.skill_title}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              fullWidth
              ChipProps={{
                color: 'secondary',
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="OPTIONAL SKILLS"
                  placeholder="Add skills..."
                  helperText="Good to have skills for this job"
                  fullWidth
                />
              )}
            />
          )}
        />
      </WrappingRowContainer>

      <Controller
        name="causes"
        control={control}
        defaultValue={[]}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            multiple
            id="causes"
            options={causes}
            value={value}
            onChange={(event, newValue) => {
              onChange(newValue);
            }}
            getOptionLabel={(option) => option.cause_title}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            fullWidth
            ChipProps={{
              color: 'secondary',
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="CAUSES"
                placeholder="Add causes..."
                helperText="Causes supported by this job"
                fullWidth
              />
            )}
          />
        )}
      />
      <Box position="relative" width="100%">
        <SelectedPostImages images={images} />
      </Box>

      <Box position="relative" width="100%">
        <SelectedPostVideos videos={videos} />
      </Box>
    </div>
  );
}

export default VolunteeringForm;
